import { useEffect, useId, useRef, useState } from 'react';
import { Bold, Heading2, Italic, Link2, List, ListOrdered, Quote, Underline } from 'lucide-react';
import type { MergeFieldDef } from '@cloudsign/shared';
import { t } from '../../lib/i18n';
import { htmlToMarkup, markupToHtml } from './markup';

/** Wraps the selection (or inserts `fallback`) between `before` and `after`. Pure so it can be tested. */
export function wrapSelection(text: string, start: number, end: number, before: string, after: string, fallback: string) {
  const selected = start === end ? fallback : text.slice(start, end);
  const next = text.slice(0, start) + before + selected + after + text.slice(end);
  return { text: next, selStart: start + before.length, selEnd: start + before.length + selected.length };
}

/** Puts `prefix` at the start of each line touched by the selection (replacing the same prefix if it is already there). */
export function prefixLines(text: string, start: number, end: number, prefix: string | ((i: number) => string)) {
  const lineStart = text.lastIndexOf('\n', start - 1) + 1;
  const nl = text.indexOf('\n', end);
  const lineEnd = nl === -1 ? text.length : nl;
  const lines = text.slice(lineStart, lineEnd).split('\n');
  const out = lines.map((l, i) => (typeof prefix === 'function' ? prefix(i) : prefix) + l.replace(/^(- |\d+\. |#{2,3} |> )/, ''));
  const block = out.join('\n');
  return { text: text.slice(0, lineStart) + block + text.slice(lineEnd), selStart: lineStart, selEnd: lineStart + block.length };
}

export interface RichTextEditorProps {
  label: string;
  /** Stored HTML (the ADR 0020 subset). */
  value: string;
  onChange: (html: string) => void;
  /** Merge fields offered (use `mergeFieldsFor(context)` from @cloudsign/shared). */
  mergeFields?: readonly MergeFieldDef[];
  hint?: string;
  error?: string | undefined;
  disabled?: boolean;
  rows?: number;
  id?: string;
}

/**
 * Rich-text box with a formatting toolbar and a merge-field menu. It edits a plain text box (no contentEditable),
 * so it works with every screen reader and keyboard, and it emits only the restricted HTML subset of ADR 0020.
 */
export function RichTextEditor({ label, value, onChange, mergeFields = [], hint, error, disabled = false, rows = 8, id }: RichTextEditorProps) {
  const auto = useId();
  const base = id ?? auto;
  const area = useRef<HTMLTextAreaElement>(null);
  const [markup, setMarkup] = useState(() => htmlToMarkup(value));
  const lastEmitted = useRef(value);
  const pendingSel = useRef<[number, number] | null>(null);

  // Take outside changes (for example "reset to default"), but not the echo of our own edits.
  useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setMarkup(htmlToMarkup(value));
    }
  }, [value]);

  useEffect(() => {
    if (pendingSel.current && area.current) {
      const [s, e] = pendingSel.current;
      area.current.focus();
      area.current.setSelectionRange(s, e);
      pendingSel.current = null;
    }
  });

  const update = (text: string) => {
    setMarkup(text);
    const html = markupToHtml(text);
    lastEmitted.current = html;
    onChange(html);
  };
  const apply = (r: { text: string; selStart: number; selEnd: number }) => {
    pendingSel.current = [r.selStart, r.selEnd];
    update(r.text);
  };
  const sel = (): [number, number] => [area.current?.selectionStart ?? markup.length, area.current?.selectionEnd ?? markup.length];
  const wrap = (before: string, after: string, fallback: string) => {
    const [s, e] = sel();
    apply(wrapSelection(markup, s, e, before, after, fallback));
  };
  const prefix = (p: string | ((i: number) => string)) => {
    const [s, e] = sel();
    apply(prefixLines(markup, s, e, p));
  };
  const link = () => {
    const [s, e] = sel();
    const text = s === e ? t('p2w.rte.linkText') : markup.slice(s, e);
    const r = wrapSelection(markup, s, e, '[', `](${t('p2w.rte.linkUrl')})`, text);
    apply({ ...r, selStart: r.selEnd + 2, selEnd: r.selEnd + 2 + t('p2w.rte.linkUrl').length });
  };
  const insertField = (path: string) => {
    const [s, e] = sel();
    const token = `{{${path}}}`;
    apply({ text: markup.slice(0, s) + token + markup.slice(e), selStart: s + token.length, selEnd: s + token.length });
  };

  const tools: { key: string; label: string; icon: typeof Bold; run: () => void }[] = [
    { key: 'b', label: t('p2w.rte.bold'), icon: Bold, run: () => wrap('**', '**', t('p2w.rte.bold').toLowerCase()) },
    { key: 'i', label: t('p2w.rte.italic'), icon: Italic, run: () => wrap('*', '*', t('p2w.rte.italic').toLowerCase()) },
    { key: 'u', label: t('p2w.rte.underline'), icon: Underline, run: () => wrap('++', '++', t('p2w.rte.underline').toLowerCase()) },
    { key: 'a', label: t('p2w.rte.link'), icon: Link2, run: link },
    { key: 'ul', label: t('p2w.rte.bullets'), icon: List, run: () => prefix('- ') },
    { key: 'ol', label: t('p2w.rte.numbers'), icon: ListOrdered, run: () => prefix((i) => `${i + 1}. `) },
    { key: 'h', label: t('p2w.rte.heading'), icon: Heading2, run: () => prefix('## ') },
    { key: 'q', label: t('p2w.rte.quote'), icon: Quote, run: () => prefix('> ') },
  ];

  const describedBy = [`${base}-help`, hint ? `${base}-hint` : '', error ? `${base}-err` : ''].filter(Boolean).join(' ');
  return (
    <div>
      <label htmlFor={`${base}-area`} className="mb-1 block text-sm font-medium text-slate-800">
        {label}
      </label>
      <div className="rounded-md border border-slate-300 bg-white">
        <div role="toolbar" aria-label={t('p2w.rte.toolbar')} aria-controls={`${base}-area`} className="flex flex-wrap items-center gap-1 border-b border-slate-200 p-1">
          {tools.map((tool) => (
            <button
              key={tool.key}
              type="button"
              title={tool.label}
              aria-label={tool.label}
              disabled={disabled}
              onClick={tool.run}
              className="inline-flex h-10 w-10 items-center justify-center rounded text-slate-700 hover:bg-slate-100 disabled:opacity-50 sm:h-8 sm:w-8"
            >
              <tool.icon aria-hidden="true" className="h-4 w-4" />
            </button>
          ))}
          {mergeFields.length > 0 ? (
            <select
              aria-label={t('p2w.rte.mergeField')}
              disabled={disabled}
              value=""
              onChange={(e) => {
                if (e.target.value) insertField(e.target.value);
              }}
              className="ml-auto min-h-10 max-w-full rounded border border-slate-300 bg-white px-2 text-sm text-slate-800 sm:min-h-8"
            >
              <option value="">{t('p2w.rte.mergeFieldPlaceholder')}</option>
              {mergeFields.map((f) => (
                <option key={f.path} value={f.path}>
                  {t(`merge.${f.path}`)}
                </option>
              ))}
            </select>
          ) : null}
        </div>
        <textarea
          ref={area}
          id={`${base}-area`}
          rows={rows}
          value={markup}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          onChange={(e) => update(e.target.value)}
          className="block w-full resize-y rounded-b-md bg-white px-3 py-2 font-mono text-sm text-slate-900 disabled:bg-slate-100"
        />
      </div>
      <p id={`${base}-help`} className="mt-1 text-xs text-slate-600">
        {t('p2w.rte.help')}
      </p>
      {hint ? (
        <p id={`${base}-hint`} className="mt-1 text-xs text-slate-600">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={`${base}-err`} className="mt-1 text-sm text-red-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}
